import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import FloatingPlannerBubble from './components/FloatingPlannerBubble';
import ScheduleVisitSection from './components/ScheduleVisitSection';

// Public Page Components
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import PropertyPage from './pages/PropertyPage';
import ApartmentPage from './pages/ApartmentPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import TeamPage from './pages/TeamPage';
// import LoginPage from './pages/LoginPage';
// import Register from './pages/Register';
import RequestViewingPage from './pages/RequestViewingPage';
import GalleryPage from './pages/GalleryPage';
import HousePlanBot from './pages/HousePlanBot';

import LoginPage from './pages/LoginPage';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import PropertiesModule from './pages/dashboard/PropertiesModule';
import ApartmentsModule from './pages/dashboard/ApartmentsModule';
import BlogsModule from './pages/dashboard/BlogsModule';
import HeroSlidesModule from './pages/dashboard/HeroSlidesModule';
import GalleryModule from './pages/dashboard/GalleryModule';
import GalleryCategoriesModule from './pages/dashboard/GalleryCategoriesModule';
import BlogCategoriesModule from './pages/dashboard/BlogCategoriesModule';
import PropertyCategoriesModule from './pages/dashboard/PropertyCategoriesModule';
import TeamModule from './pages/dashboard/TeamModule';
import TeamRolesModule from './pages/dashboard/TeamRolesModule';
import ListingTeamModule from './pages/dashboard/ListingTeamModule';
import BoardMembersModule from './pages/dashboard/BoardMembersModule';
import BookingsModule from './pages/dashboard/BookingsModule';
import TestimonialsModule from './pages/dashboard/TestimonialsModule';
import ContactMessagesModule from './pages/dashboard/ContactMessagesModule';
import UsersModule from './pages/dashboard/UsersModule';

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <CurrencyProvider>
              <LanguageProvider>
                <DataProvider>
                  <AppContent />
                </DataProvider>
              </LanguageProvider>
            </CurrencyProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const showNav = !location.pathname.startsWith('/dashboard') && location.pathname !== '/login' && location.pathname !== '/register';
  const showFooter = !location.pathname.startsWith('/dashboard') && location.pathname !== '/login' && location.pathname !== '/register';
  const showPlannerBubble = !location.pathname.startsWith('/dashboard') && location.pathname !== '/ai-planner';
  const pagesWithInlineSchedule = ['/', '/contact', '/request-viewing'];
  const showGlobalScheduleSection = showFooter && !pagesWithInlineSchedule.includes(location.pathname);


  return (
    <>
      {showNav && <Navigation />}
      <Routes>
        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/request-viewing" element={<RequestViewingPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/ai-planner" element={<HousePlanBot />} />
        
        {/* Properties/Apartments Routes */}
        <Route path="/property" element={<PropertyPage />} />
        <Route path="/properties" element={<PropertyPage />} />
        <Route path="/property/:id" element={<PropertyDetailPage />} />
        <Route path="/apartment" element={<ApartmentPage />} />
        <Route path="/apartment/:id" element={<PropertyDetailPage />} />
        
        {/* Blog Routes */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogDetailPage />} />
        
        {/* Dashboard Routes */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="properties" element={<PropertiesModule />} />
          <Route path="apartments" element={<ApartmentsModule />} />
          <Route path="blogs" element={<BlogsModule />} />
          <Route path="hero-slides" element={<HeroSlidesModule />} />
          <Route path="gallery" element={<GalleryModule />} />
          <Route path="gallery-categories" element={<GalleryCategoriesModule />} />
          <Route path="blog-categories" element={<BlogCategoriesModule />} />
          <Route path="property-categories" element={<PropertyCategoriesModule />} />
          <Route path="team" element={<TeamModule />} />
          <Route path="team-roles" element={<TeamRolesModule />} />
          <Route path="board-members" element={<BoardMembersModule />} />
          <Route path="listing-team" element={<ListingTeamModule />} />
          <Route path="bookings" element={<BookingsModule />} />
          <Route path="contact-messages" element={<ContactMessagesModule />} />
          <Route path="testimonials" element={<TestimonialsModule />} />
          <Route path="users" element={<UsersModule />} />
        </Route>
      </Routes>
      {showPlannerBubble && <FloatingPlannerBubble />}
      {showGlobalScheduleSection && <ScheduleVisitSection />}
      {showFooter && <Footer />}
    </>
  );
}

export default App;
